import * as moment from 'moment';
import * as React from 'react';
import ReactDatePicker from 'react-datepicker';

import { dateParser } from '@common/dateParser';
import { CILNotificationViewModel } from '@services/CILNotificationService';

export enum CILNotificationReplacementComponentPage {
  Replacement,
  ReplacementAcceptance
}

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface CILNotificationReplacementComponentProperties {
  readonly date?: Date;
  readonly isDateInvalid: boolean;
  readonly isSubmitEnabled: boolean;
  readonly page: CILNotificationReplacementComponentPage;
  readonly viewModel: CILNotificationViewModel;

  readonly checkBox: () => void;
  readonly checkDate: (date: Date) => void;
  readonly checkSignature: (signature: string) => void;
  readonly gotoPage: (page: CILNotificationReplacementComponentPage) => void;
  readonly leave: () => void;
  readonly submitReplacement: () => void;
}

// Component definition
export class CILNotificationReplacementComponent extends React.PureComponent<
  CILNotificationReplacementComponentProperties
> {
  public render(): JSX.Element {
    return this.props.page === CILNotificationReplacementComponentPage.Replacement
      ? this.renderReplacement()
      : this.renderReplacementAcceptance();
  }

  private getDeliveryDateString(): string {
    let estimatedDeliveryDate: string = '[estimated delivery date missing]';
    if (this.props.viewModel.Est_Delivery_Dt) {
      estimatedDeliveryDate = moment(this.props.viewModel.Est_Delivery_Dt).format('MM/DD/YYYY');
    }
    return estimatedDeliveryDate;
  }

  private renderReplacement(): JSX.Element {
    return (
      <div className="text-left">
        <h2>Option #1</h2>
        <br />
        <ul className="list-unstyled">
          <li>
            <strong>Supplier:</strong> {this.props.viewModel.Supplier_Nm}
          </li>
        </ul>
        <p>We are offering to replace your unit with {this.props.viewModel.Appliance_Type}.</p>
        <ul className="list-unstyled">
          <li>
            Go to:{' '}
            <a href={this.props.viewModel.Supplier_Product_URL} target="_blank">
              {this.props.viewModel.Supplier_Product_URL}
            </a>
          </li>
          <li>
            Enter <strong>Item/Model #: {this.props.viewModel.Product_SKU}</strong> into the search box and hit "enter"
          </li>
          <li>
            <strong>Estimated Delivery Date:</strong> {this.getDeliveryDateString()}
          </li>
        </ul>
        <h4>
          <strong>Terms and Conditions:</strong>
        </h4>
        <ul>
          <li>
            Replacements will be of similar features, capacity, and efficiency as the item being replaced. FNHW is not
            responsible for matching brands, color, and/or exact dimensions (unless contract states otherwise)
          </li>
          <li>
            It is your responsibility to ensure the new unit will fit into the designated space before accepting the
            replacement unit
          </li>
          <ul>
            <li>
              (We highly suggest you use a measuring tape to measure your old unit and compare to the measurements of
              the new unit which are displayed online)
            </li>
          </ul>
          <li>
            FNHW does not cover <em>any</em> modification costs that may be necessary to replace/install the new
            appliance (Including cutting, cabinetry, tile, granite, etc.)
          </li>
        </ul>
        <h4>
          <u>Return Policy</u>:
        </h4>
        <ul>
          <li>
            If you choose to return the unit, <em>for any reason</em> (unless defective or damaged, which must be
            reported within 48 hours of delivery), you are responsible to pay any applicable{' '}
            <strong>re-stocking fees</strong> of up to 35% of the purchase price
          </li>
          <li>No returns will be accepted after the unit has been installed</li>
          <li>
            *<strong>Note:</strong> If you wish to purchase a different unit than the one being offered, you must accept
            cash-in-lieu option (<u>Option #2</u>) to purchase the unit of your choice
          </li>
        </ul>
        <h4>If you select replacement options:</h4>
        <ol>
          <li>
            FNHW will process and send an order to: {this.props.viewModel.Supplier_Nm} Store #:{' '}
            {this.props.viewModel.Supplier_Store_Num} (if applicable)
          </li>
          <li>
            {this.props.viewModel.Supplier_Nm} will contact you within 3-5 business days from (estimated delivery date
            above) to schedule the delivery, installation, and disposal (<strong>if applicable</strong>) of your old
            unit
          </li>
        </ol>
        <div className="text-center">
          <button
            className="btn btn-primary"
            onClick={() => this.props.gotoPage(CILNotificationReplacementComponentPage.ReplacementAcceptance)}
          >
            Select Replacement
          </button>
          <button className="btn btn-secondary" onClick={this.props.leave}>
            View Option #2: Cash-in-Lieu
          </button>
        </div>
      </div>
    );
  }

  private renderReplacementAcceptance(): JSX.Element {
    if (!this.props.viewModel) {
      return (
        <div className="alert alert-danger" role="alert">
          <strong>Not Found!</strong>
        </div>
      );
    }

    let datePickerClasses = this.props.isDateInvalid ? { className: 'is-invalid' } : { className: '' };

    return (
      <div className="text-left">
        <h2>Option #1</h2>
        <br />
        <ul className="list-unstyled">
          <li>
            <strong>Replacement Unit:</strong> {this.props.viewModel.Appliance_Type}
          </li>
          <li>
            <strong>Replacement Item/Model#:</strong> {this.props.viewModel.Product_SKU}
          </li>
          <li>
            <strong>Estimated Delivery Date:</strong> {this.getDeliveryDateString()}
          </li>
        </ul>
        <p>
          {this.props.viewModel.Supplier_Nm} will contact you within 3-5 business days from{' '}
          {this.getDeliveryDateString()} to schedule the delivery, installation, and disposal of your old unit
          (comprehensive contracts only).
        </p>

        <div className="row middle-xs">
          <div className="col-sm-1 col-xs-2 text-center">
            <input onClick={this.handleCheckBox} type="checkbox" />
          </div>
          <div className="col-sm-11 col-xs-10">
            <p>
              I understand that FNHW does not cover any modification costs necessary to replace/install the appliance.
              (ie: cutting cabinetry, tile, granite, etc…)
            </p>
          </div>
        </div>
        <div className="row middle-xs">
          <div className="col-sm-1 col-xs-2 text-center">
            <input onClick={this.handleCheckBox} type="checkbox" />
          </div>
          <div className="col-sm-11 col-xs-10">
            <p>
              I understand it is my responsibility to ensure the new appliance will fit into the designated space. (
              <i>We suggest you measure your old unit and compare to the measurements of the new unit online</i>)
            </p>
          </div>
        </div>
        <div className="row middle-xs">
          <div className="col-sm-1 col-xs-2 text-center">
            <input onClick={this.handleCheckBox} type="checkbox" />
          </div>
          <div className="col-sm-11 col-xs-10">
            <p>
              I understand once the unit is delivered, if I choose to return it for ANY reason (other than
              defective/damaged) I will be responsible for any re-stocking fees incurred by{' '}
              <b>({this.props.viewModel.Supplier_Nm || '*supplier*'})</b> of{' '}
              <u>
                <b>up to 35%</b> of the purchase price.
              </u>
            </p>
          </div>
        </div>
        <hr />
        <p>*I have reviewed the unit being offered above and authorize FNHW to purchase the new unit.</p>
        <div className="form-control">
          <label>Contract Holder</label>
          <input onChange={event => this.props.checkSignature(event.currentTarget.value)} type="text" />
        </div>
        <div className="form-control">
          <label>Date</label>
          <ReactDatePicker
            {...datePickerClasses}
            onChange={m => m && this.props.checkDate(m.toDate())}
            selected={this.props.date && dateParser.parse(this.props.date)}
            value={this.props.date && dateParser.parse(this.props.date).format('MM/DD/YYYY')}
          />
          {this.props.isDateInvalid && (
            <div className="error">
              <p>Please enter today's date</p>
            </div>
          )}
        </div>
        <div className="text-center">
          <button
            className="btn btn-primary"
            disabled={!this.props.isSubmitEnabled}
            onClick={this.props.submitReplacement}
          >
            I AGREE / SUBMIT
          </button>
          <button className="btn btn-secondary" onClick={this.props.leave}>
            View Option #2: Cash-in-Lieu
          </button>
        </div>
      </div>
    );
  }

  private handleCheckBox = (event: React.MouseEvent<HTMLInputElement>) => {
    event.currentTarget.disabled = true;
    this.props.checkBox();
  };
}



// WEBPACK FOOTER //
// ./src/cil-notification/_components/replacement/_components/replacement.component.tsx